import PageHero from "@/components/PageHero";
import { Container, FadeIn, SectionLabel, SectionHeading, SectionDivider } from "@/components/Section";
import SEOHead from "@/components/SEOHead";
import NextStepsBlock from "@/components/NextStepsBlock";
import AcuityScheduleBlock from "@/components/AcuityScheduleBlock";
import PartnersStrip from "@/components/PartnersStrip";
import SocialEcosystem from "@/components/SocialEcosystem";
import { IMG } from "@/lib/data";
import { GraduationCap, Sparkles, Zap, Trophy, Users, Shield } from "lucide-react";

export default function AfterSchoolClubs() {
  return (
    <div data-testid="after-school-clubs-page" className="bg-[#050505]">
      <SEOHead
        title="After School Clubs · Immersive Creative Tech for 7–16s · Temple of Kulture"
        description="Weekly after-school clubs in Medway and Kent. Projection mapping, AI storytelling, XR, music tech, immersive play for ages 7–16. Funded places, SEND-friendly, school partnerships welcome."
        image={IMG.dance1}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "EducationalOccupationalProgram",
          name: "Temple of Kulture — After School Clubs",
          provider: { "@type": "Organization", name: "Temple of Kulture" },
          educationalProgramMode: "after-school",
          audience: { "@type": "PeopleAudience", suggestedMinAge: 7, suggestedMaxAge: 16 },
        }}
      />
      <PageHero
        eyebrow="7 – 16 years"
        title="Wednesdays just got better."
        kicker="After School Clubs"
        copy="Weekly clubs where young people make immersive worlds. Projection mapping, AI storytelling, XR, music tech, movement and design — taught by working artists."
        bg={IMG.dance1}
        focalMobile="50% 25%"
        focalDesktop="50% 35%"
        primaryCta={{ to: "#register-interest", label: "Register interest" }}
        secondaryCta={{ to: "/community", label: "School partnerships" }}
      />

      <section className="py-24 sm:py-28 bg-zinc-950 border-y border-amber-500/10">
        <Container>
          <FadeIn className="max-w-3xl">
            <SectionLabel>Why after school</SectionLabel>
            <SectionHeading className="mt-5">
              The hours <span className="text-amber-400">between 3pm and 6pm</span> shape a young person.
            </SectionHeading>
            <p className="mt-6 text-zinc-300 text-lg leading-relaxed">
              Those three hours decide whether a child finds a creative
              community or scrolls alone. Our after-school clubs hold that
              window open — with the tools, the artists and the peer group
              that turn a Wednesday into a turning point.
            </p>
          </FadeIn>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <Card Icon={Sparkles} t="Projection mapping" d="Light, geometry, video — build worlds on walls." />
            <Card Icon={Zap} t="AI storytelling" d="Write characters with AI. Learn to use it consciously." />
            <Card Icon={GraduationCap} t="Music tech & sound" d="Ableton fundamentals, sound design, immersive audio." />
            <Card Icon={Users} t="Movement & immersive" d="Bodies in space — dance meets projection design." />
            <Card Icon={Trophy} t="Showcase nights" d="Every term ends with a public showcase. Family welcome." />
            <Card Icon={Shield} t="DBS-checked, SEND-friendly" d="Sensory-aware sessions, BSL on request, accessibility first." />
          </div>
        </Container>
      </section>

      <SectionDivider label="Register interest" />
      <Container className="pb-20 max-w-5xl" id="register-interest">
        <div className="grid lg:grid-cols-2 gap-4">
          <AcuityScheduleBlock
            eyebrow="Families"
            title="Book a free trial session"
            copy="One session, no obligation. Meet the artists, see the space, ask anything."
            slug="after-school-clubs"
            audience="7 – 16"
            duration="90 mins"
            fallbackTo="/join"
            fallbackLabel="Join the waitlist"
            testId="acuity-asc-trial"
          />
          <AcuityScheduleBlock
            eyebrow="Schools & partners"
            title="Bring clubs to your school"
            copy="We deliver in primaries, secondaries and alternative provision across Medway & Kent."
            slug="schools-and-send"
            audience="Schools"
            duration="Half-day"
            fallbackTo="/community"
            fallbackLabel="Send a brief"
            testId="acuity-asc-schools"
          />
        </div>
        <p className="mt-8 text-xs text-zinc-500 leading-relaxed max-w-xl">
          Funded and pay-what-you-can places available for families on lower
          incomes — no need to ask why. Just tick the box on the form.
        </p>
      </Container>

      <SocialEcosystem
        eyebrow="See it in action"
        title="Clubs in the wild"
        copy="Short cuts from this term's showcases, builds and rehearsals — across every platform."
      />

      <section className="py-20 sm:py-24 bg-zinc-950 border-t border-amber-500/10">
        <PartnersStrip
          eyebrow="Funded · supported · in partnership with"
          title="Real councils, real schools, real funders"
          copy="Our after-school clubs run alongside Medway Council, Kent County Council, Medway Youth and Arts Council England."
          programmeKey="community"
          variant="card"
          testId="asc-partners"
        />
      </section>

      <NextStepsBlock
        title="Continue the journey"
        eyebrow="Where to next"
        steps={[
          { label: "Younger sibling", title: "Interactive Playground (under 12)", desc: "Projection-floor play for our youngest.", to: "/interactive-playground", cta: "Discover", testid: "asc-next-ip" },
          { label: "16+", title: "LEVEL UP development pathway", desc: "When school years end, the journey continues.", to: "/level-up-xr", cta: "Apply", testid: "asc-next-level-up" },
          { label: "Home learning", title: "Home Education programme", desc: "Wed/Thu day-time sessions for families.", to: "/home-education", cta: "See dates", testid: "asc-next-home-ed" },
        ]}
      />
    </div>
  );
}

function Card({ Icon, t, d }) {
  return (
    <div className="border border-white/5 bg-black p-6">
      <Icon className="h-6 w-6 text-amber-400" />
      <p className="mt-4 font-display font-bold text-base">{t}</p>
      <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{d}</p>
    </div>
  );
}
